import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Badge from '@cloudscape-design/components/badge';
import { useNavigate } from 'react-router-dom';
import { InsightsTheme } from '../types/ticket';
import { recordEvent } from '../rum';

interface ThemesSectionProps {
  themes: InsightsTheme[];
  onGenerate?: () => void;
  generating?: boolean;
}

function ThemeCard({ theme, onClick }: { theme: InsightsTheme; onClick: () => void }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f4f4f4'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent'; }}
      style={{
        border: '1px solid #e9ebed',
        borderRadius: '8px',
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box fontWeight="bold">{theme.title}</Box>
        <Badge color="blue">{theme.ticketCount}</Badge>
      </div>
      <Box variant="small" color="text-body-secondary">
        {theme.ticketCount} ticket{theme.ticketCount !== 1 ? 's' : ''} • Click to view
      </Box>
    </div>
  );
}

export function ThemesSection({ themes, onGenerate, generating }: ThemesSectionProps) {
  const navigate = useNavigate();

  const handleThemeClick = (theme: InsightsTheme) => {
    recordEvent('theme_click', { source: 'dashboard', ticketCount: theme.ticketCount });
    const params = new URLSearchParams();
    params.set('ticketIds', theme.ticketIds.join(','));
    params.set('themeTitle', theme.title);
    navigate(`/tickets?${params.toString()}`);
  };

  if (!themes.length) {
    return (
      <Container
        header={
          <Header
            variant="h2"
            actions={onGenerate ? <Button onClick={onGenerate} loading={generating}>Generate Themes</Button> : undefined}
          >
            Patterns & Themes
          </Header>
        }
      >
        <Box color="text-body-secondary">
          No patterns detected yet. Click "Generate Themes" to analyze recent tickets for recurring issues.
        </Box>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          counter={`(${themes.length})`}
          actions={onGenerate ? <Button onClick={onGenerate} loading={generating} variant="icon" iconName="refresh" /> : undefined}
        >
          Patterns & Themes
        </Header>
      }
    >
      <ExpandableSection headerText={`${themes.length} recurring patterns detected (last 8 weeks)`} defaultExpanded>
        <ColumnLayout columns={3}>
          {themes.map((theme, i) => (
            <ThemeCard key={i} theme={theme} onClick={() => handleThemeClick(theme)} />
          ))}
        </ColumnLayout>
      </ExpandableSection>
    </Container>
  );
}
